import apiClient from './api';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const cache = new Map<string, CacheEntry<any>>();
const pendingRequests = new Map<string, Promise<any>>();

/**
 * Fetch reference data with in-memory caching and request deduplication.
 * Cached entries expire after CACHE_TTL_MS (5 minutes).
 * Concurrent calls for the same key share a single network request.
 */
export async function fetchReferenceData<T>(key: string, url: string): Promise<T> {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data as T;
  }

  // Deduplicate concurrent requests for the same key
  const pending = pendingRequests.get(key);
  if (pending) {
    return pending as Promise<T>;
  }

  const request = apiClient.get(url).then((response) => {
    const data = response.data.data || response.data;
    cache.set(key, { data, timestamp: Date.now() });
    pendingRequests.delete(key);
    return data as T;
  }).catch((error) => {
    pendingRequests.delete(key);
    throw error;
  });

  pendingRequests.set(key, request);
  return request as Promise<T>;
}

/**
 * Clear a specific cache entry or the entire cache.
 */
export function clearReferenceDataCache(key?: string): void {
  if (key) {
    cache.delete(key);
  } else {
    cache.clear();
  }
}
